import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { connect } from 'react-redux'

import { fetchListBanners } from 'actions/ContentActions'
import { resetSessionId } from 'actions/CruiseActions'

import { BANNER_LOCATION_CRUISES } from 'constants/bannerLocations'
import { CRUISE_UNIFIED_FILTERS } from 'constants/cruise'

import { getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'
import { getListName } from 'analytics/enhanced-ecommerce'

import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOfferList from 'hooks/Offers/useOfferList'

import Sheet from 'components/utils/Sheet'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import LayoutContainer from 'components/Common/LayoutContainer'

import CruiseHeadData from '../Common/CruiseHeadData'
import CruisePageSearchHero from './CruisePageSearchHero'
import CruisePageDestinationsSection from './CruisePageDestinationsSection'
import CruisePageCruiseLinesSection from './CruisePageCruiseLinesSection/CruisePageCruiseLinesSection'
import CruisePageFlashOffersCarousel from './CruisePageFlashOffersCarousel'
import CruisePageDealsTileCarousel from './CruisePageDealsTileCarousel'
import CruisePageSpecialOffersCarousel from './CruisePageSpecialOffersCarousel'
import CruisePageLastMinuteOffersCarousel from './CruisePageLastMinuteOffersCarousel'
import CruisePageShortTermCruisesCarousel from './CruisePageShortTermCruisesCarousel'
import CruisePageLongHaulOceanCruisesCarousel from './CruisePageLongHaulOceanCruisesCarousel'
import CruisePageLuxuryPremiumCruisesCarousel from './CruisePageLuxuryPremiumCruisesCarousel'
import CruisePageRiverCruisesCarousel from './CruisePageRiverCruisesCarousel'
import CruisePageBrowseByPorts from './CruisePageBrowseByPorts'
import CruisePageDepartureFromCarousel from './CruisePageDepartureFromCarousel/CruisePageDepartureFromCarousel'
import CruisePageDeparturesSection from './CruisePageDeparturesSection'
import CruisePageUSPsSection from './CruisePageUSPsSection'
import CruisePageExpeditionCruisesCarousel from './CruisePageExpeditionCruisesCarousel'

interface Props {
  banners?: Array<App.ListBanner>
}

function CruisesPage(props: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_CRUISES))
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const banners = props.banners || []

  const createCruiseListName = (sectionName: string) => getListName('OfferList', 'CRUISE', sectionName)

  const heroTracking = useOfferListTracking(createCruiseListName('Hero Carousel'))
  const todaysTopCruiseTracking = useOfferListTracking(createCruiseListName('Todays Top Cruises'))
  const departingFromTracking = useOfferListTracking(createCruiseListName('Departing From'))
  const bestValueTracking = useOfferListTracking(createCruiseListName('Best Value'))
  const lastMinuteTracking = useOfferListTracking(createCruiseListName('Last Minute Offers'))
  const destinationsTracking = useOfferListTracking(createCruiseListName('Popular cruise destinations'))
  const cruiseLinesTracking = useOfferListTracking(createCruiseListName('Featured Cruise Lines'))
  const shortTermCruisesTracking = useOfferListTracking(createCruiseListName('Short Term Cruises'))
  const longHaulOceanCruisesTracking = useOfferListTracking(createCruiseListName('Long-haul Ocean Cruises'))
  const luxuryPremiumCruisesTracking = useOfferListTracking(createCruiseListName('Luxury & Premium Cruises'))
  const riverCruisesTracking = useOfferListTracking(createCruiseListName('River Cruises'))
  const departuresTracking = useOfferListTracking(createCruiseListName('Top Ports to Start Your Adventure'))
  const expeditionCruisesTracking = useOfferListTracking(createCruiseListName('Expedition Cruises'))

  const flashOfferList = useOfferList(CRUISE_UNIFIED_FILTERS)

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const initialSearchState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialSearchState,
  })

  useEffect(() => {
    dispatch(resetSessionId())
  }, [dispatch, globalCruisesSearchDispatch])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
      <CruiseHeadData />

      <GlobalSearchContextProvider
        state={globalCruisesSearchState}
        dispatch={globalCruisesSearchDispatch}
      >
        <CruisePageSearchHero />

        <Sheet padding="24 0">
          <Group direction="vertical" gap={56}>
            <OfferListEventsProvider tracking={heroTracking}>
              <ScrollAnchor id="deals-carousel" />
              <CruisePageDealsTileCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={todaysTopCruiseTracking}>
              <CruisePageFlashOffersCarousel offerList={flashOfferList} />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={cruiseLinesTracking}>
              <CruisePageCruiseLinesSection />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={destinationsTracking}>
              <CruisePageDestinationsSection />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={lastMinuteTracking}>
              <CruisePageLastMinuteOffersCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={departingFromTracking}>
              <CruisePageDepartureFromCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={departuresTracking}>
              <CruisePageDeparturesSection />
            </OfferListEventsProvider>

            {banners && <Leaderboard banners={banners} />}
            <CruisePageUSPsSection />

            <OfferListEventsProvider tracking={shortTermCruisesTracking}>
              <CruisePageShortTermCruisesCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={longHaulOceanCruisesTracking}>
              <CruisePageLongHaulOceanCruisesCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={luxuryPremiumCruisesTracking}>
              <CruisePageLuxuryPremiumCruisesCarousel />
            </OfferListEventsProvider>

            <LayoutContainer>
              <SupportTile description={intl.formatMessage({
                id: 'pFOvv6ZwcPPByHe9mCHO',
                defaultMessage: 'Call our dedicated customer service team anytime.',
                description: 'Support tile description on cruises page in the cruise section',
              })}
              />
            </LayoutContainer>

            <OfferListEventsProvider tracking={riverCruisesTracking}>
              <CruisePageRiverCruisesCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={bestValueTracking}>
              <CruisePageSpecialOffersCarousel />
            </OfferListEventsProvider>

            <OfferListEventsProvider tracking={expeditionCruisesTracking}>
              <CruisePageExpeditionCruisesCarousel />
            </OfferListEventsProvider>

            <CruisePageBrowseByPorts />
          </Group>
        </Sheet>
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    banners: state.content.listBanners.cruises,
  }
}

export default connect(mapStateToProps)(CruisesPage)
